import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ScanFace, Search } from "lucide-react";
import api from "lib/api";
import { useI18n } from "i18n";
import { EVENT_CATEGORIES, categoryLabel, championshipLabel } from "lib/events";
import {
  EventCard,
  EventGridSkeleton,
  EmptyState,
} from "components/public/EventCard";
import CopyLinkButton from "components/public/CopyLinkButton";
import { shareUrl } from "lib/share";
import {
  SEARCH_PAGE_SIZE,
  completeSearchResults,
} from "lib/faceSearch";
import SelfieSearchModal from "components/public/SelfieSearchModal";
import {
  Lightbox,
  PhotoGridSkeleton,
  PhotoTile,
} from "components/public/PhotoGrid";

const WHEN_OPTIONS = ["", "active", "upcoming", "past"];

/**
 * Home da AREA PUBLICA: vitrine dos eventos.
 *
 * Consome apenas `/api/public/…`. Para o visitante anonimo a resposta traz so
 * eventos `published`; se houver um operador/admin logado, o backend inclui
 * tambem rascunhos e arquivados (previa do proprio acervo) e o card ganha o
 * chip do status — ver `photoe/views/public.py:published_events`.
 *
 * Tambem faz a busca por selfie em TODOS os eventos (dentro dos filtros
 * escolhidos). Como na galeria do evento, o resultado e compartilhavel:
 * `/?search=<uuid>` reabre as mesmas fotos e os mesmos filtros.
 */
export default function PublicHome() {
  const { t, lang } = useI18n();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [city, setCity] = useState("");
  const [when, setWhen] = useState("");
  const [championship, setChampionship] = useState("");
  // Campeonatos com pelo menos um evento publicado (o backend ja filtra).
  const [championships, setChampionships] = useState([]);
  // Cidades ofertadas no filtro: as que de fato tem evento publicado. Derivadas
  // da primeira carga, para o filtro nunca oferecer uma opcao sem resultado.
  const [cities, setCities] = useState([]);

  // Busca por selfie: null = vitrine de eventos; lista = fotos encontradas.
  const [matches, setMatches] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [lightbox, setLightbox] = useState(null);
  // Modal da selfie e o erro dela (mostrado dentro do modal).
  const [selfieOpen, setSelfieOpen] = useState(false);
  const [selfieError, setSelfieError] = useState("");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchId = searchParams.get("search");
  // Busca cujo resultado esta (ou esta indo) para a tela — ver EventGallery.
  const loadedSearch = useRef(null);
  const showingMatches = matches !== null;

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await api.get("/api/public/events/", {
        params: {
          search: search || undefined,
          category: category || undefined,
          city: city || undefined,
          when: when || undefined,
          championship: championship || undefined,
        },
      });
      const data = res.data || [];
      setEvents(data);
      // Sem filtro de cidade ativo, a resposta cobre todas — e a hora de
      // reconstruir a lista de opcoes.
      if (!city) {
        setCities(
          [...new Set(data.map((e) => e.city).filter(Boolean))].sort((a, b) =>
            a.localeCompare(b)
          )
        );
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [search, category, city, when, championship]);

  // A busca textual so dispara depois que o usuario para de digitar.
  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    api
      .get("/api/public/championships/")
      .then((res) => setChampionships(res.data || []))
      .catch(() => setChampionships([]));
  }, []);

  // Link compartilhado (`?search=<uuid>`): reabre o resultado guardado e os
  // filtros com que ele foi feito. Sem o parametro, volta a vitrine.
  useEffect(() => {
    if (!searchId) {
      if (loadedSearch.current) {
        loadedSearch.current = null;
        setMatches(null);
      }
      return;
    }
    if (searchId === loadedSearch.current) return;
    loadedSearch.current = searchId;
    setSearching(true);
    setSearchError("");
    api
      .get(`/api/public/face-searches/${searchId}/`, {
        params: { limit: SEARCH_PAGE_SIZE },
      })
      .then(async (res) => {
        if (loadedSearch.current !== searchId) return;
        const data = res.data || {};
        // Busca feita dentro de um evento: o lugar dela e a galeria dele.
        if (data.event) {
          navigate(`/events/${data.event}?search=${searchId}`, {
            replace: true,
          });
          return;
        }
        const f = data.filters || {};
        setSearch(f.search || "");
        setCategory(f.category || "");
        setCity(f.city || "");
        setWhen(f.when || "");
        setChampionship(f.championship || "");
        const results = await completeSearchResults(data);
        if (loadedSearch.current !== searchId) return;
        setMatches(results);
      })
      .catch(() => {
        if (loadedSearch.current !== searchId) return;
        loadedSearch.current = null;
        setMatches(null);
        setSearchError(t("public.search.sharedNotFound"));
        setSearchParams({}, { replace: true });
      })
      .finally(() => setSearching(false));
  }, [searchId, navigate, t, setSearchParams]);

  const searchByFace = async (file) => {
    if (!file) return;
    setSearching(true);
    setSearchError("");
    setSelfieError("");
    try {
      const form = new FormData();
      form.append("file", file);
      // Os filtros da vitrine delimitam em quais eventos procurar.
      const filters = { search, category, city, when, championship };
      Object.entries(filters).forEach(([key, value]) => {
        if (value) form.append(key, value);
      });
      const res = await api.post("/api/public/search-faces/", form, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { limit: SEARCH_PAGE_SIZE },
      });
      const data = res.data || {};
      setMatches(await completeSearchResults(data));
      loadedSearch.current = data.search_id || null;
      setSearchParams(data.search_id ? { search: data.search_id } : {});
      setSelfieOpen(false);
    } catch (err) {
      // O modal fica aberto com o motivo, para tentar outra imagem.
      setSelfieError(err.response?.data?.detail || t("public.search.error"));
    } finally {
      setSearching(false);
    }
  };

  const clearMatches = () => {
    loadedSearch.current = null;
    setMatches(null);
    setSearchError("");
    setSearchParams({});
  };

  return (
    <>
      <section className="border-b border-neutral-200 bg-gradient-to-br from-brand-wash via-white to-white">
        <div className="mx-auto w-full max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
          <h1 className="max-w-3xl text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
            {t("public.home.headline")}
          </h1>
          <p className="mt-3 max-w-2xl text-base text-neutral-600">
            {t("public.home.subtitle")}
          </p>

          {/* O filtro de campeonato so entra na grade quando existe algum
              publicado — senao seria um seletor vazio na vitrine. */}
          <div
            className={`mt-8 grid w-full gap-3 md:grid-cols-2 ${
              championships.length
                ? "lg:grid-cols-[minmax(0,2fr)_repeat(4,minmax(0,1fr))]"
                : "lg:grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))]"
            }`}
          >
            <div className="relative">
              <Search
                size={18}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("public.home.searchPlaceholder")}
                aria-label={t("public.home.searchPlaceholder")}
                className="h-12 w-full rounded-xl border border-neutral-200 bg-white pl-10 pr-4 text-sm text-neutral-900 shadow-sm outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
              />
            </div>

            {championships.length > 0 && (
              <select
                value={championship}
                onChange={(e) => setChampionship(e.target.value)}
                aria-label={t("championship.title")}
                className="h-12 w-full rounded-xl border border-neutral-200 bg-white px-4 text-sm text-neutral-900 shadow-sm outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
              >
                <option value="">{t("public.home.allChampionships")}</option>
                {championships.map((item) => (
                  <option key={item.id} value={item.slug}>
                    {championshipLabel(item)}
                  </option>
                ))}
              </select>
            )}

            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              aria-label={t("event.category")}
              className="h-12 w-full rounded-xl border border-neutral-200 bg-white px-4 text-sm text-neutral-900 shadow-sm outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
            >
              <option value="">{t("public.home.allCategories")}</option>
              {EVENT_CATEGORIES.map((value) => (
                <option key={value} value={value}>
                  {categoryLabel(t, value)}
                </option>
              ))}
            </select>

            <select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              aria-label={t("common.city")}
              className="h-12 w-full rounded-xl border border-neutral-200 bg-white px-4 text-sm text-neutral-900 shadow-sm outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
            >
              <option value="">{t("public.home.allCities")}</option>
              {cities.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>

            <select
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              aria-label={t("public.home.when")}
              className="h-12 w-full rounded-xl border border-neutral-200 bg-white px-4 text-sm text-neutral-900 shadow-sm outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
            >
              {WHEN_OPTIONS.map((value) => (
                <option key={value || "all"} value={value}>
                  {t(`public.home.when.${value || "all"}`)}
                </option>
              ))}
            </select>
          </div>

          {/* ------------------ encontre suas fotos ------------------ */}
          <div className="mt-4 flex w-full flex-col gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-base font-semibold text-neutral-900">
                <ScanFace size={20} className="text-amber-600" />
                {t("public.home.selfieTitle")}
              </h2>
              <p className="mt-1 max-w-2xl text-sm text-neutral-600">
                {t("public.home.selfieHint")}
              </p>
            </div>

            <div className="flex shrink-0 flex-wrap items-center gap-3">
              {showingMatches && searchId && (
                <CopyLinkButton
                  url={() => shareUrl("/", { search: searchId })}
                  label={t("public.search.copyLink")}
                />
              )}
              {showingMatches && (
                <button
                  type="button"
                  onClick={clearMatches}
                  className="rounded-full border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
                >
                  {t("public.home.showEvents")}
                </button>
              )}
              <button
                type="button"
                disabled={searching}
                onClick={() => {
                  setSelfieError("");
                  setSelfieOpen(true);
                }}
                className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-600 disabled:opacity-60"
              >
                {searching ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <ScanFace size={16} />
                )}
                {t("public.search.action")}
              </button>
            </div>
          </div>

          {searchError && (
            <p className="mt-3 w-full rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {searchError}
            </p>
          )}
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6">
        {searching && !showingMatches ? (
          <PhotoGridSkeleton />
        ) : showingMatches ? (
          <>
            <h2 className="mb-5 text-lg font-semibold text-neutral-900">
              {t("public.search.resultsTitle")}
              <span className="ml-2 text-sm font-normal text-neutral-500">
                {matches.length}
              </span>
            </h2>
            {matches.length === 0 ? (
              <EmptyState
                title={t("public.search.noResults")}
                hint={t("public.search.noResultsHint")}
              />
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {matches.map((photo, index) => (
                  <PhotoTile
                    key={photo.id}
                    photo={photo}
                    onOpen={() => setLightbox(index)}
                    t={t}
                  />
                ))}
              </div>
            )}
          </>
        ) : loading ? (
          <EventGridSkeleton />
        ) : error ? (
          <EmptyState
            title={t("common.loadError")}
            hint={t("public.home.loadErrorHint")}
            action={
              <button
                type="button"
                onClick={load}
                className="rounded-full bg-amber-500 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-600"
              >
                {t("login.configRetry")}
              </button>
            }
          />
        ) : events.length === 0 ? (
          <EmptyState
            title={t("public.home.empty")}
            hint={t("public.home.emptyHint")}
          />
        ) : (
          <>
            <h2 className="mb-5 text-lg font-semibold text-neutral-900">
              {t("public.home.eventsTitle")}
              <span className="ml-2 text-sm font-normal text-neutral-500">
                {events.length}
              </span>
            </h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {events.map((event) => (
                <EventCard key={event.id} event={event} lang={lang} t={t} />
              ))}
            </div>
          </>
        )}
      </section>

      {showingMatches && lightbox !== null && matches[lightbox] && (
        <Lightbox
          photos={matches}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onNavigate={setLightbox}
          hasMore={false}
          onNeedMore={() => {}}
          t={t}
        />
      )}

      <SelfieSearchModal
        open={selfieOpen}
        onClose={() => setSelfieOpen(false)}
        onSelect={searchByFace}
        busy={searching}
        error={selfieError}
      />
    </>
  );
}

